import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Parse from 'html-react-parser';
import {
  Row,
  Col,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIclientCreate } from '../../api/Clients';

import ProcessingSpinner from '../ProcessingSpinner';

import {
  getStateFromUrl,
  removeUrlParamFromUrl,
} from '../../helpers';

const ExternalAuthSignupForm = ({
  email,
  integration,
  setView,
}) => {
  const intl = useIntl();

  const initialFormFields = {
    first_name: '',
    last_name: '',
    email,
    subdomain: '',
  };
  const [formFields, setFormFields] = useState(initialFormFields);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormFields(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    setErrors({});
    const verrors = {};
    Object.keys(formFields).forEach((key) => {
      if (!formFields[key]) {
        verrors[key] = [];
        verrors[key].push(intl.formatMessage({ defaultMessage: 'The {key} field is required.' }, { key: key.replace('_', ' ') }));
      }
    });
    setErrors(verrors);
    return Object.keys(verrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const currentState = getStateFromUrl();
      const response = await APIclientCreate({
        ...formFields,
        type: 'main',
        integration_id: integration.id,
        source: integration.id,
        state: btoa(JSON.stringify({
          ...currentState,
          redirect_url: removeUrlParamFromUrl(window.location.href, 'view'),
          integration,
        })),
      });
      if (response?.status === 'success') {
        setView('signed-up');
      }
    } catch (response) {
      const { data } = response.response;
      if (data.errors) {
        setErrors(data.errors);
      }
    } finally {
      setSaving(false);
    }
  };

  const emailError = errors.email ?
    errors.email[0].replace(
      '<help-link />',
      `<a href="https://stockpress.co/sp-sign-up/" target="_blank">
        ${intl.formatMessage({ defaultMessage: 'click here' })}
      </a>`,
    ) : '';

  return (
    <Form
      className="signup-form"
      noValidate
      onChange={handleChange}
      onSubmit={handleSubmit}
    >
      <h5>{intl.formatMessage({ defaultMessage: 'Create your account' })}</h5>
      <Row form>
        <Col md={6}>
          <FormGroup>
            <Label for="first_name">
              {intl.formatMessage({ defaultMessage: 'First name *' })}
            </Label>
            <Input
              required
              autoFocus
              id="first_name"
              name="first_name"
              placeholder={intl.formatMessage({ defaultMessage: 'Jane' })}
              disabled={saving}
              invalid={!!errors.first_name}
            />
            <FormFeedback>{errors.first_name}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="last_name">
              {intl.formatMessage({ defaultMessage: 'Last name *' })}
            </Label>
            <Input
              required
              id="last_name"
              name="last_name"
              placeholder={intl.formatMessage({ defaultMessage: 'Doe' })}
              disabled={saving}
              invalid={!!errors.last_name}
            />
            <FormFeedback>{errors.last_name}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>
      <Row form>
        <Col>
          <FormGroup>
            <Label for="email">
              {intl.formatMessage({ defaultMessage: 'Work email address *' })}
            </Label>
            <Input
              required
              id="email"
              name="email"
              placeholder={intl.formatMessage({ defaultMessage: 'jane@companyname.com' })}
              defaultValue={email}
              disabled={saving}
              invalid={!!errors.email}
            />
            <FormFeedback>{Parse(emailError)}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>
      <Row form>
        <Col>
          <FormGroup className="signup-subdomain">
            <Label for="subdomain">
              {intl.formatMessage({ defaultMessage: 'Workspace URL *' })}
            </Label>
            <div>
              <Input
                required
                id="subdomain"
                name="subdomain"
                placeholder={intl.formatMessage({ defaultMessage: 'companyname' })}
                disabled={saving}
                invalid={!!errors.subdomain}
              />
              <span>.{process.env.MIX_APP_DOMAIN}</span>
            </div>
            <FormFeedback>{errors.subdomain}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>
      <Row form>
        <Col>
          <FormGroup className="center">
            <Button
              type="submit"
              disabled={saving}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Sign up' })}
            </Button>
          </FormGroup>
        </Col>
      </Row>
      <Row form>
        <Col className="center disclaimer mt-2">
          {intl.formatMessage({ defaultMessage: 'By submitting this form, you agree to our' })}{' '}
          {intl.formatMessage({
            defaultMessage: '<termsLink>Terms of Use</termsLink> and <policyLink>Privacy Policy</policyLink>',
          }, {
            termsLink: msg => (<a href="https://stockpress.co/terms-of-use/" target="_blank" rel="noreferrer">{msg}</a>),
            policyLink: msg => (<a href="https://stockpress.co/privacy-policy/" target="_blank" rel="noreferrer">{msg}</a>),
          })}
        </Col>
      </Row>
      <Row form>
        <Col className="center mt-4">
          <a
            href="#back"
            onClick={(e) => {
              e.preventDefault();
              setView('main');
            }}
          >
            <i className="far fa-arrow-left" /> {intl.formatMessage({ defaultMessage: 'Back' })}
          </a>
        </Col>
      </Row>
    </Form>
  );
};

ExternalAuthSignupForm.propTypes = {
  email: PropTypes.string,
  integration: PropTypes.object.isRequired,
  setView: PropTypes.func.isRequired,
};

ExternalAuthSignupForm.defaultProps = {
  email: '',
};

export default ExternalAuthSignupForm;
